import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import {
  Home, Plus, Edit, Trash2, Upload, LogOut, Building2, Mail,
  TrendingUp, X, Image as ImageIcon, Loader2, Eye, FileEdit
} from 'lucide-react';
import { propertiesAPI, contactAPI, authAPI, imageUrl } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { SiteContentEditor } from '../components/SiteContentEditor';

const emptyForm = {
  title_sq: '', title_en: '',
  location_sq: '', location_en: '',
  description_sq: '', description_en: '',
  price: '', type: 'sale',
  bedrooms: '', bathrooms: '', area: '',
  featured: true,
};

export const AdminDashboard = () => {
  const { toast } = useToast();
  const [properties, setProperties] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [selectedImages, setSelectedImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [messageToView, setMessageToView] = useState(null);

  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      window.location.href = '/admin';
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [props, msgs] = await Promise.all([
        propertiesAPI.list(),
        contactAPI.listMessages(),
      ]);
      setProperties(props);
      setMessages(msgs);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    window.location.href = '/';
  };

  const openCreateForm = () => {
    setEditingProperty(null);
    setFormData(emptyForm);
    setSelectedImages([]);
    setShowForm(true);
  };

  const openEditForm = (property) => {
    setEditingProperty(property);
    setFormData({
      title_sq: property.title.sq,
      title_en: property.title.en,
      location_sq: property.location.sq,
      location_en: property.location.en,
      description_sq: property.description.sq,
      description_en: property.description.en,
      price: property.price,
      type: property.type,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      area: property.area,
      featured: property.featured,
    });
    setSelectedImages([]);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        fd.append(k, v);
      });
      selectedImages.forEach((img) => fd.append('images', img));

      if (editingProperty) {
        await propertiesAPI.update(editingProperty.id, fd);
        toast({ title: 'Property Updated', description: 'Changes saved successfully' });
      } else {
        await propertiesAPI.create(fd);
        toast({ title: 'Property Created', description: 'New property added successfully' });
      }
      setShowForm(false);
      loadData();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to save property',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await propertiesAPI.delete(deleteConfirm.id);
      toast({ title: 'Deleted', description: 'Property removed successfully' });
      setDeleteConfirm(null);
      loadData();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleRemoveImage = async (propertyId, imgUrl) => {
    try {
      await propertiesAPI.removeImage(propertyId, imgUrl);
      toast({ title: 'Image removed' });
      loadData();
      if (editingProperty && editingProperty.id === propertyId) {
        const updated = await propertiesAPI.get(propertyId);
        setEditingProperty(updated);
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to remove image', variant: 'destructive' });
    }
  };

  const handleMarkMessageRead = async (msgId) => {
    try {
      await contactAPI.updateStatus(msgId, 'read');
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteMessage = async (msgId) => {
    try {
      await contactAPI.deleteMessage(msgId);
      toast({ title: 'Message deleted' });
      loadData();
    } catch (err) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const unreadCount = messages.filter(m => m.status === 'new').length;

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-black/90 backdrop-blur-2xl border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 flex items-center justify-center p-1.5">
                <img src="/logo.png" alt="Kaya" className="h-full w-auto object-contain" />
              </div>
              <div className="border-l border-white/20 pl-4">
                <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-sm text-[#C5A05E]">Property Management</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => (window.location.href = '/')}
                className="backdrop-blur-xl bg-white/5 border border-white/10 hover:border-[#C5A05E] hover:bg-[#C5A05E]/10 text-white"
                data-testid="admin-view-site-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                View Site
              </Button>
              <Button
                onClick={handleLogout}
                className="bg-red-600/20 border border-red-500/30 hover:bg-red-600/30 text-red-400"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Total Properties', value: properties.length, icon: Building2, gradient: 'from-[#C5A05E] to-[#D4B06E]' },
            { label: 'For Sale', value: properties.filter(p => p.type === 'sale').length, icon: Home, gradient: 'from-[#D4B06E] to-[#C5A05E]' },
            { label: 'For Rent', value: properties.filter(p => p.type === 'rent').length, icon: TrendingUp, gradient: 'from-[#B39050] to-[#C5A05E]' },
            { label: 'New Messages', value: unreadCount, icon: Mail, gradient: 'from-[#C5A05E] to-[#B39050]' },
          ].map((stat, index) => (
            <Card key={index} className="backdrop-blur-xl bg-white/5 border border-white/10 hover:border-[#C5A05E]/50 transition-all group">
              <div className={`h-1.5 bg-gradient-to-r ${stat.gradient}`} />
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium uppercase tracking-wider">{stat.label}</p>
                    <p className="text-4xl font-bold text-white mt-2">{stat.value}</p>
                  </div>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-r ${stat.gradient} p-[2px] group-hover:scale-110 transition-transform`}>
                    <div className="w-full h-full bg-black/80 rounded-2xl flex items-center justify-center">
                      <stat.icon className="w-7 h-7 text-[#C5A05E]" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="properties" className="space-y-6">
          <TabsList className="bg-white/5 backdrop-blur-xl border border-white/10 p-1.5">
            <TabsTrigger value="properties" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C5A05E] data-[state=active]:to-[#D4B06E] data-[state=active]:text-white text-gray-400" data-testid="admin-tab-properties">
              <Building2 className="w-4 h-4 mr-2" />
              Properties
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C5A05E] data-[state=active]:to-[#D4B06E] data-[state=active]:text-white text-gray-400" data-testid="admin-tab-messages">
              <Mail className="w-4 h-4 mr-2" />
              Messages {unreadCount > 0 && <span className="ml-2 px-2 py-0.5 text-xs bg-red-500 rounded-full">{unreadCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C5A05E] data-[state=active]:to-[#D4B06E] data-[state=active]:text-white text-gray-400" data-testid="admin-tab-content">
              <FileEdit className="w-4 h-4 mr-2" />
              Site Content
            </TabsTrigger>
          </TabsList>

          {/* Properties Tab */}
          <TabsContent value="properties">
            <Card className="backdrop-blur-xl bg-white/5 border border-white/10">
              <CardHeader className="border-b border-white/10">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl font-bold text-white">Manage Properties</CardTitle>
                  <Button
                    onClick={openCreateForm}
                    className="bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] hover:scale-105 transition-transform text-white font-bold"
                    data-testid="admin-add-property-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Property
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {loading ? (
                  <div className="text-center py-12 text-gray-400">
                    <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin" />
                    Loading...
                  </div>
                ) : properties.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-40" />
                    No properties yet. Click "Add Property" to create one.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {properties.map((property) => (
                      <Card key={property.id} className="backdrop-blur-xl bg-white/5 border border-white/10 hover:border-[#C5A05E]/30 transition-all">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-6">
                            <img
                              src={property.images && property.images.length > 0 ? imageUrl(property.images[0]) : 'https://placehold.co/128?text=No+Image'}
                              alt={property.title.sq}
                              className="w-32 h-32 object-cover rounded-xl border border-white/10"
                            />
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h3 className="text-xl font-bold text-white">{property.title.sq}</h3>
                                  <p className="text-gray-400 text-sm mt-1">{property.location.sq}</p>
                                </div>
                                <Badge className={property.type === 'sale' ? 'bg-[#C5A05E]/20 text-[#C5A05E] border border-[#C5A05E]/30' : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'}>
                                  {property.type === 'sale' ? 'For Sale' : 'For Rent'}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-4 gap-4 mb-4">
                                <div>
                                  <p className="text-xs text-gray-500 uppercase">Price</p>
                                  <p className="font-bold text-[#C5A05E]">€{property.price?.toLocaleString()}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 uppercase">Bedrooms</p>
                                  <p className="font-semibold text-white">{property.bedrooms}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 uppercase">Bathrooms</p>
                                  <p className="font-semibold text-white">{property.bathrooms}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 uppercase">Area</p>
                                  <p className="font-semibold text-white">{property.area} m²</p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => openEditForm(property)}
                                  className="backdrop-blur-xl bg-white/5 border border-[#C5A05E]/30 hover:bg-[#C5A05E]/20 text-[#C5A05E]"
                                  data-testid={`admin-edit-property-${property.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => setDeleteConfirm(property)}
                                  className="bg-red-600/20 border border-red-500/30 hover:bg-red-600/30 text-red-400"
                                  data-testid={`admin-delete-property-${property.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card className="backdrop-blur-xl bg-white/5 border border-white/10">
              <CardHeader className="border-b border-white/10">
                <CardTitle className="text-2xl font-bold text-white">Contact Messages</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {loading ? (
                  <div className="text-center py-12"><Loader2 className="w-8 h-8 mx-auto animate-spin text-[#C5A05E]" /></div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Mail className="w-16 h-16 mx-auto mb-4 opacity-40" />
                    <p className="text-lg">No messages yet</p>
                    <p className="text-sm mt-2">Messages from the contact form will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <Card key={msg.id} className={`backdrop-blur-xl border transition-all ${msg.status === 'new' ? 'bg-[#C5A05E]/10 border-[#C5A05E]/30' : 'bg-white/5 border-white/10'}`}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-3 mb-1">
                                <h3 className="text-lg font-bold text-white">{msg.name}</h3>
                                {msg.status === 'new' && <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">NEW</Badge>}
                              </div>
                              <p className="text-sm text-gray-400">{msg.email}</p>
                              {msg.phone && <p className="text-sm text-gray-500">{msg.phone}</p>}
                            </div>
                            <p className="text-xs text-gray-500">{new Date(msg.created_at).toLocaleString()}</p>
                          </div>
                          <p className="text-gray-300 leading-relaxed mb-4 whitespace-pre-wrap">{msg.message}</p>
                          <div className="flex gap-2">
                            {msg.status === 'new' && (
                              <Button size="sm" onClick={() => handleMarkMessageRead(msg.id)} className="backdrop-blur-xl bg-white/5 border border-white/20 hover:bg-white/10 text-white">
                                <Eye className="w-4 h-4 mr-1" />
                                Mark Read
                              </Button>
                            )}
                            <Button size="sm" onClick={() => handleDeleteMessage(msg.id)} className="bg-red-600/20 border border-red-500/30 hover:bg-red-600/30 text-red-400">
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Site Content Tab */}
          <TabsContent value="content">
            <SiteContentEditor />
          </TabsContent>
        </Tabs>
      </div>

      {/* Property Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-900 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">
              {editingProperty ? 'Edit Property' : 'Add New Property'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-300 mb-1 block">Title (Albanian)</label>
                <Input required value={formData.title_sq} onChange={(e) => setFormData({ ...formData, title_sq: e.target.value })} className="bg-white/5 border-white/20 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1 block">Title (English)</label>
                <Input required value={formData.title_en} onChange={(e) => setFormData({ ...formData, title_en: e.target.value })} className="bg-white/5 border-white/20 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1 block">Location (Albanian)</label>
                <Input required value={formData.location_sq} onChange={(e) => setFormData({ ...formData, location_sq: e.target.value })} className="bg-white/5 border-white/20 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1 block">Location (English)</label>
                <Input required value={formData.location_en} onChange={(e) => setFormData({ ...formData, location_en: e.target.value })} className="bg-white/5 border-white/20 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1 block">Price (€)</label>
                <Input type="number" required value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="bg-white/5 border-white/20 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1 block">Type</label>
                <select required value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full bg-white/5 border border-white/20 text-white rounded-md px-3 py-2">
                  <option value="sale" className="bg-slate-900">For Sale</option>
                  <option value="rent" className="bg-slate-900">For Rent</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1 block">Bedrooms</label>
                <Input type="number" required value={formData.bedrooms} onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })} className="bg-white/5 border-white/20 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1 block">Bathrooms</label>
                <Input type="number" required value={formData.bathrooms} onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })} className="bg-white/5 border-white/20 text-white" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1 block">Area (m²)</label>
                <Input type="number" required value={formData.area} onChange={(e) => setFormData({ ...formData, area: e.target.value })} className="bg-white/5 border-white/20 text-white" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" id="featured" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} className="w-4 h-4 accent-[#C5A05E]" />
                <label htmlFor="featured" className="text-sm text-gray-300">Featured Property</label>
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-300 mb-1 block">Description (Albanian)</label>
              <Textarea required rows={3} value={formData.description_sq} onChange={(e) => setFormData({ ...formData, description_sq: e.target.value })} className="bg-white/5 border-white/20 text-white" />
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1 block">Description (English)</label>
              <Textarea required rows={3} value={formData.description_en} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} className="bg-white/5 border-white/20 text-white" />
            </div>

            {/* Image Upload */}
            <div>
              <label className="text-sm text-gray-300 mb-2 block">
                {editingProperty ? 'Add More Images' : 'Property Images'}
              </label>
              <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-[#C5A05E]/50 transition-colors">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => setSelectedImages(Array.from(e.target.files))}
                  className="hidden"
                  id="image-upload"
                />
                <label htmlFor="image-upload" className="cursor-pointer">
                  <ImageIcon className="w-12 h-12 mx-auto text-[#C5A05E] mb-2" />
                  <p className="text-white font-semibold">Click to upload images</p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP (max 10MB each)</p>
                </label>
                {selectedImages.length > 0 && (
                  <p className="mt-3 text-sm text-[#C5A05E]">{selectedImages.length} file(s) selected</p>
                )}
              </div>
            </div>

            {/* Existing Images (Edit mode) */}
            {editingProperty && editingProperty.images && editingProperty.images.length > 0 && (
              <div>
                <label className="text-sm text-gray-300 mb-2 block">Existing Images</label>
                <div className="grid grid-cols-4 gap-2">
                  {editingProperty.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img src={imageUrl(img)} alt="" className="w-full h-24 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(editingProperty.id, img)}
                        className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter className="pt-4 gap-2">
              <Button type="button" onClick={() => setShowForm(false)} className="backdrop-blur-xl bg-white/5 border border-white/20 text-white hover:bg-white/10">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] text-white font-bold" data-testid="admin-save-property">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Upload className="w-4 h-4 mr-2" />{editingProperty ? 'Update' : 'Save'} Property</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent className="bg-slate-900 border border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to delete "{deleteConfirm?.title?.sq}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/20 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Toaster />
    </div>
  );
};
